import styled from 'styled-components';

import { Badge } from '@components/badge';
import { FormBuilder } from '@components/form-builder';
import { useFormBuilderContext } from '@components/form-builder/context';
import { ElementsList } from '@components/form-builder/elements-list';
import { getFieldConfigForKind } from '@components/form-builder/field-registry';
import { FullscreenPreview } from '@components/form-builder/fullscreen-preview';
import { GroupAccordion } from '@components/form-builder/group-accordion';
import { FieldType, FormV2 } from '@components/form/v2';
import { useDeleteModal } from '@components/generic-modals';
import { IconAttributeJourney } from '@components/icons/attribute-journey';
import { IconAttributeTraveller } from '@components/icons/attribute-traveller';
import { IconCheckmarkSmall } from '@components/icons/checkmark-small';
import { IconDecimalFloat } from '@components/icons/decimal-float';
import { IconMoreVertical } from '@components/icons/more-vertical';
import { IconSettings } from '@components/icons/settings';
import { IconTrash } from '@components/icons/trash';
import { Spinner } from '@components/loading-spinner';
import { Menu } from '@components/menu';
import { hideOverlay, useOverlay } from '@components/overlay';
import { Stack } from '@components/stack';
import { Switch } from '@components/switch';
import { showError } from '@components/toaster';
import { useScopedTranslate } from '@locale/translate';
import { theme } from '@styles/theme';
import { pxToRem } from '@utils/converters';

import { useMarketplaceIntegrationLists } from 'api/app-marketplace/hooks';
import { useCompanyTravellerAttributeTemplates } from 'api/attributes/hooks';
import { AttributeType, TravellerAttributeTemplate } from 'api/attributes/requests';
import {
  useCreateJourneyFormFile,
  useCreateJourneyFormImage,
  useDeleteJourneyForm,
  useJourneyForm,
  usePublishJourneyForm,
  useUnpublishJourneyForm,
  useUpdateJourneyForm,
} from 'api/journey-forms/hooks';
import { JourneyForm } from 'api/journey-forms/requests';
import { useJourneyAttributes, useUpdateAttribute } from 'api/journeys/hooks';
import { useFormBuilderLists } from 'api/lists/hooks';
import { FavoriteMenuItem } from 'ui/authenticated/main/favorites/favorites';
import { ShareJourneyFormButton } from 'ui/authenticated/main/pages/journeys/share-journey-form-button';
import { useFeature } from 'ui/hooks';
import { isInTemplateRoute } from 'ui/router';
import { isSelectFieldType } from 'utils/attribute-field-types';

import { getAvailableAttributes } from './available-attributes';
import { JourneyFormSettingsPage } from './settings-tab';
import {
  attributeTypeToAllowedFieldTypes,
  getSidebarAttributeBuilderProps,
} from './sidebar-attribute-builder-props';
import { attributeTypeToFieldType } from './use-create-default-journey-form';

export const useEditJourneyFormOverlay = () => {
  return useOverlay(EditJourneyFormModal);
};

const EditJourneyFormModal = ({
  journeyId,
  formId,
  onClose,
}: {
  journeyId: string;
  formId: string;
  onClose?: () => void;
}) => {
  const { t } = useScopedTranslate(locale);
  const isTemplate = isInTemplateRoute();
  const { isEnabled: hasKennitalaEnabled } = useFeature('FEATURE_KENNITALA');
  const formQuery = useJourneyForm(journeyId, formId);
  const { data: lists, isLoading: listsLoading } = useFormBuilderLists();
  const { data: marketplaceLists, isLoading: marketplaceListsLoading } =
    useMarketplaceIntegrationLists();
  const updateMutation = useUpdateJourneyForm(formId);
  const updateAttributeMutation = useUpdateAttribute(journeyId);
  const createFormTemplateImageQuery = useCreateJourneyFormImage();
  const createFormFileQuery = useCreateJourneyFormFile();
  const ctaQuery = useCompanyTravellerAttributeTemplates({ systemAttributes: true });
  const jtaQuery = useJourneyAttributes(isTemplate ? '' : journeyId);

  const isAuthLoading =
    !isTemplate &&
    (listsLoading || marketplaceListsLoading || ctaQuery.isLoading || jtaQuery.isLoading);

  if (formQuery.isLoading || isAuthLoading) {
    return <Spinner />;
  }

  if (formQuery.isError) {
    showError(t('formNotFound'));
    onClose?.();
    hideOverlay();
    return null;
  }

  if (!formQuery.isSuccess || (!isTemplate && (!ctaQuery.isSuccess || !jtaQuery.isSuccess))) {
    return <Spinner />;
  }

  if (isTemplate) {
    return (
      <FullscreenPreview
        name={formQuery.data.name}
        fields={formQuery.data.fieldsConfig.fields}
        onClose={() => {
          onClose?.();
          hideOverlay();
        }}
      />
    );
  }

  const extraTabs = [
    {
      section: 'settings',
      label: t('settings'),
      content: (
        <JourneyFormSettingsPage
          form={formQuery.data}
          isAutoStartOnChange={(isAutoStart) => {
            updateMutation.mutate({
              ...formQuery.data,
              isAutoStart,
            });
          }}
          portalOnChange={(isPortalForm) => {
            updateMutation.mutate({
              ...formQuery.data,
              isPortalForm,
            });
          }}
          onUpdate={(updates) => {
            updateMutation.mutate({
              ...formQuery.data,
              ...updates,
            });
          }}
        />
      ),
      icon: <IconSettings />,
    },
  ];

  const journeyForm = formQuery.data;

  // Process form fields to apply proper builderProps based on attributes
  // The API should do this correctly from now on but we can do it here just in case
  const processFormFields = (form: FormV2): FormV2 => {
    const processField = (field: FieldType): FieldType => {
      let updatedField = { ...field };

      // Process nested fields in sections
      if (field.kind === 'section' && field.fields) {
        (updatedField as typeof field).fields = field.fields.map(processField);
      }

      // Apply builderProps for fields with journey attributes
      if ('journeyAttributeId' in field && field.journeyAttributeId && jtaQuery.data) {
        const attribute = jtaQuery.data.find((attr) => attr.shortId === field.journeyAttributeId);
        if (attribute) {
          updatedField.builderProps = {
            ...field.builderProps,
            canEditKind: attributeTypeToAllowedFieldTypes[attribute.attributeType],
            canEditOptions: isSelectFieldType(attribute.attributeType),
          };
        }
      }

      // Apply builderProps for fields with company attributes
      if ('companyAttributeId' in field && field.companyAttributeId && ctaQuery.data) {
        const attribute = ctaQuery.data.find((attr) => attr.shortId === field.companyAttributeId);
        if (attribute) {
          const isRequiredSystemAttribute =
            attribute.isSystemAttribute &&
            (attribute.title === 'Full name' || attribute.title === 'Email');
          updatedField.builderProps = {
            ...field.builderProps,
            canEditKind: attributeTypeToAllowedFieldTypes[attribute.attributeType],
            canEditRequired: !isRequiredSystemAttribute,
            canRemove: !isRequiredSystemAttribute,
            canEditConditions: !isRequiredSystemAttribute,
            canDuplicate: !isRequiredSystemAttribute,
            canEditDefaultValue: !isRequiredSystemAttribute,
          };
        }
      }

      return updatedField;
    };

    return {
      ...form,
      fields: form.fields.map(processField),
    };
  };

  const processedForm = processFormFields(journeyForm.fieldsConfig);

  return (
    <Container>
      <FormBuilder
        formVariant="journey"
        name={journeyForm.name}
        description={journeyForm.description}
        form={processedForm}
        showKennitala={hasKennitalaEnabled}
        submissionMessage={journeyForm.submissionMessageConfig}
        predefinedOptionsLists={lists ?? []}
        marketplaceIntegrationLists={marketplaceLists ?? []}
        extraFieldGroups={<JourneyFieldsGroup journeyId={journeyId} />}
        onJourneyAttributeListChange={(journeyAttributeId, listId) => {
          updateAttributeMutation.mutate({
            shortId: journeyAttributeId,
            data: { options: listId },
          });
        }}
        onSave={async (template, onSaveSuccess) => {
          const response = await updateMutation.mutateAsync({
            ...journeyForm,
            ...template,
          });
          onSaveSuccess?.(response.fieldsConfig, response.submissionMessageConfig);
        }}
        onImageSave={async (props) => {
          const response = await createFormTemplateImageQuery.mutateAsync(props);
          return response.imageId;
        }}
        onFileSave={async (props) => {
          return createFormFileQuery.mutateAsync(props);
        }}
        extraTabs={extraTabs}
        extraTopActions={<ExtraTopActions journeyForm={formQuery.data} />}
        secondaryActions={<FormBuilderIsActiveSwitch journeyForm={formQuery.data} />}
        onComplete={onClose}
        useTabs
      />
    </Container>
  );
};

const ExtraTopActions = ({ journeyForm }: { journeyForm: JourneyForm }) => {
  return (
    <>
      <ShareJourneyFormButton variant="secondary" journeyForm={journeyForm} />
      <FormActions form={journeyForm} />
    </>
  );
};

const FormActions = ({ form }: { form: JourneyForm }) => {
  const { t } = useScopedTranslate(locale);

  const showDelete = useDeleteModal();
  const deleteMutation = useDeleteJourneyForm(form);
  const publishMutation = usePublishJourneyForm(form);
  const unpublishMutation = useUnpublishJourneyForm(form);
  const updateMutation = useUpdateJourneyForm(form.shortId);

  const handleToggle = (key: keyof JourneyForm, value: boolean) => {
    updateMutation.mutate({ ...form, [key]: value });
  };

  return (
    <Menu
      buttonProps={{ iconLeft: <IconMoreVertical />, variant: 'secondary' }}
      placement="left-start"
    >
      <FavoriteMenuItem shortId={form.shortId} contentType="journeyform" />
      <Menu.Separator />
      <Menu.Switch
        label={t('active')}
        value={form.isPublished}
        onChange={() => {
          return form.isPublished ? unpublishMutation.mutateAsync() : publishMutation.mutateAsync();
        }}
      />

      <Menu.Switch
        label={t('startOnSubmission')}
        value={form.isAutoStart}
        onChange={() => handleToggle('isAutoStart', !form.isAutoStart)}
      />

      <Menu.Separator />
      <Menu.Item
        label={t('core.delete')}
        icon={<IconTrash />}
        variant="danger"
        onSelect={() =>
          showDelete({
            title: t('removeTitle', { name: form.name }),
            description: t('removeDescription', { name: form.name }),
            buttonText: t('core.delete'),
            hasUserConfirmation: true,
            onConfirm: () => {
              deleteMutation.mutateAsync(undefined, { onSuccess: hideOverlay });
            },
          })
        }
      />
    </Menu>
  );
};

const FormBuilderIsActiveSwitch = ({ journeyForm }: { journeyForm: JourneyForm }) => {
  const { t } = useScopedTranslate(locale);
  const publishMutation = usePublishJourneyForm(journeyForm);
  const unpublishMutation = useUnpublishJourneyForm(journeyForm);

  return (
    <Switch
      value={journeyForm.isPublished}
      label={t('active')}
      labelSide="left"
      onChange={() =>
        journeyForm.isPublished ? unpublishMutation.mutate() : publishMutation.mutate()
      }
    />
  );
};

const JourneyFieldsGroup = ({ journeyId }: { journeyId: string }) => {
  const {
    store: { useFlattenedList },
  } = useFormBuilderContext();
  const { t } = useScopedTranslate(locale);
  const isTemplate = isInTemplateRoute();
  const flattened = useFlattenedList(true);
  const ctaQuery = useCompanyTravellerAttributeTemplates({ systemAttributes: true });
  const jtaQuery = useJourneyAttributes(journeyId);

  if (isTemplate) {
    return null;
  }

  if (!ctaQuery.data || !jtaQuery.data) {
    return <Spinner />;
  }

  let existingJourneyIds: Array<string | undefined> = [];
  let existingCompanyIds: Array<string | undefined> = [];
  flattened.forEach((field) => {
    if ('journeyAttributeId' in field && field.journeyAttributeId) {
      existingJourneyIds.push(field.journeyAttributeId);
    } else if ('companyAttributeId' in field && field.companyAttributeId) {
      existingCompanyIds.push(field.companyAttributeId);
    }
  });

  const availableJourneyAttributes = getAvailableAttributes(jtaQuery.data, existingJourneyIds);
  const availableCompanyAttributes = getAvailableAttributes(ctaQuery.data, existingCompanyIds);

  const allAddedBadge = (
    <Badge variant="neutral" size="md" icon={<IconCheckmarkSmall />} text={t('allAdded')} />
  );

  return (
    <ElementsList.FieldGroup label={t('fields')}>
      <GroupAccordion
        label={t('journeyFields')}
        icon={IconAttributeJourney}
        headerEnd={availableJourneyAttributes.length === 0 ? allAddedBadge : undefined}
      >
        {availableJourneyAttributes.map((attribute) => {
          return (
            <RenderElement
              key={attribute.shortId}
              attribute={attribute}
              journeyAttributeId={attribute.shortId}
            />
          );
        })}
      </GroupAccordion>
      <GroupAccordion
        label={t('travellerFields')}
        icon={IconAttributeTraveller}
        headerEnd={availableCompanyAttributes.length === 0 ? allAddedBadge : undefined}
      >
        {availableCompanyAttributes.map((attribute) => {
          return (
            <RenderElement
              key={attribute.shortId}
              attribute={attribute}
              companyAttributeId={attribute.shortId}
            />
          );
        })}
      </GroupAccordion>
    </ElementsList.FieldGroup>
  );
};

const RenderElement = ({
  attribute,
  journeyAttributeId,
  companyAttributeId,
}: {
  attribute: AttributeType | TravellerAttributeTemplate;
  journeyAttributeId?: string;
  companyAttributeId?: string;
}) => {
  const { predefinedOptionsLists } = useFormBuilderContext();
  const kind = attributeTypeToFieldType[attribute.attributeType];
  if (!kind) {
    return null;
  }

  const config = getFieldConfigForKind(kind);
  const IconToUse = attribute.attributeType === 'decimal' ? IconDecimalFloat : config.Icon;

  // We need to add the "options" locally if we're adding one of the journey form attributes back in
  const list = predefinedOptionsLists.find(
    (list) => list.value === attribute.options && list.isHidden,
  );

  const builderProps = getSidebarAttributeBuilderProps({
    attribute,
    kind,
    isJourneyField: Boolean(journeyAttributeId),
    hasHiddenOptionsList: Boolean(list),
  });

  return (
    <ElementsList.FieldGroup.AddElement
      kind={kind}
      dragId={attribute.shortId}
      key={attribute.shortId}
      createProps={{
        label: attribute.title,
        journeyAttributeId,
        companyAttributeId,
        builderProps,

        ...(kind === 'input-file' && {
          allowMultiple: attribute.attributeType === 'file_multi',
        }),
        ...(kind === 'input-number' && {
          isDecimal: attribute.attributeType === 'decimal',
        }),

        // @ts-expect-error if it isnt a dropdown then it will be undefined anyway
        options: list?.options,
        optionsFromListId: attribute.options,
      }}
      Icon={(props) => <IconToUse {...props} color="elementOrange200" />}
      label={attribute.title}
      color="orangeTrans10"
    />
  );
};

const Container = styled(Stack)({
  position: 'fixed',
  top: pxToRem(24),
  left: pxToRem(24),
  right: pxToRem(24),
  bottom: pxToRem(24),
  backgroundColor: theme.color.backgroundLightDark,
  borderRadius: pxToRem(16),
  boxShadow: theme.shadow.fancy,
  overflow: 'hidden',
});

export const locale = {
  en: {
    settings: 'Settings',
    active: 'Active',
    portal: 'Portal',
    fields: 'Fields',
    journeyFields: 'Journey fields',
    travellerFields: 'Traveller fields',
    allAdded: 'All added',
    addToFavourites: 'Add to favourites',
    startOnSubmission: 'Start on submission',
    autoStartEnabled: 'Auto start enabled',
    autoStartDisabled: 'Auto start disabled',
    removeTitle: 'Delete external form',
    removeDescription: 'Are you sure you want to delete this external form?',
    unsavedChangesTitle: 'Unsaved Changes',
    unsavedChangesDescription: 'You have unsaved changes. Do you want to save them?',
    saveForm: 'Save Form',
    formNotFound: 'Form not found. It may have been deleted.',
  },
};
